/**
 * Sanitizes text strings recursively inside objects or arrays.
 * Strips out script tags and inline event handlers to prevent XSS.
 * Allows safe HTML for rich text fields.
 *
 * ponytail: manual regex-based stripping avoids installing DOMPurify/dependencies.
 * ceiling: highly complex obfuscated XSS payloads could bypass simple regex. Upgrade to DOMPurify if richer HTML is needed.
 */
export const sanitizeHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    return html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/on\w+\s*=\s*(?:["'][^"']*["']|[^\s>]+)/gi, '')
        .replace(/javascript:/gi, '');
};

export const sanitizePayload = (data, richFields = ['description', 'full_description', 'short_description']) => {
    if (typeof data === 'string') {
        // Strip basic XSS vectors
        let sanitized = data.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
            .replace(/on\w+\s*=\s*(?:["'][^"']*["']|[^\s>]+)/gi, '')
            .replace(/javascript:/gi, '');
        return sanitized;
    }
    
    if (Array.isArray(data)) {
        return data.map(item => sanitizePayload(item, richFields));
    }
    
    if (data !== null && typeof data === 'object') {
        const sanitizedObj = {};
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                if (richFields.includes(key)) {
                    sanitizedObj[key] = sanitizePayload(data[key], richFields);
                } else if (typeof data[key] === 'string') {
                    // For non-rich fields, we could strip all HTML, but stripping scripts is a good baseline
                    // to still allow harmless characters like < and > (e.g. "Age < 18")
                    sanitizedObj[key] = sanitizePayload(data[key], richFields).replace(/<[^>]*>?/gm, (match) => {
                         // Don't strip if it looks like math (e.g. < 50)
                         if (match.match(/^<\s*\d/)) return match;
                         return '';
                    });
                } else {
                    sanitizedObj[key] = sanitizePayload(data[key], richFields);
                }
            }
        }
        return sanitizedObj;
    }
    
    return data;
};

/**
 * Obfuscates / masks an email address for privacy and security in verification modals.
 * Example: "raj9693018569@gmail.com" -> "r***569@gmail.com"
 * Example: "john.doe@example.com" -> "j***e@example.com"
 * Example: "ab@gmail.com" -> "a*@gmail.com"
 */
export const maskEmail = (email) => {
    if (!email || typeof email !== 'string' || !email.includes('@')) return email || '';
    const [name, domain] = email.split('@');
    if (name.length <= 2) {
        return `${name[0]}*@${domain}`;
    }
    if (name.length <= 5) {
        return `${name[0]}***${name[name.length - 1]}@${domain}`;
    }
    const start = name[0];
    const end = name.slice(-3);
    return `${start}***${end}@${domain}`;
};
